/* Nutrise Auto Gift v2.2
 * Synchronizes app-owned campaign rewards with the cart. Shopify's regular
 * discount system remains untouched.
 */
(() => {
  'use strict';

  if (window.NutriseAutoGift) return;

  const root = window.Shopify?.routes?.root || '/';
  const endpoint = `${root}apps/nutrise-auto-gift`;
  const nativeFetch = window.fetch.bind(window);
  const properties = {
    marker: '_nutrise_auto_gift',
    rule: '_nutrise_auto_gift_rule',
    tier: '_nutrise_auto_gift_tier',
    access: '_nutrise_auto_gift_access',
    reward: '_nutrise_auto_gift_reward',
    value: '_nutrise_auto_gift_value',
  };
  const sessionTokenKey = 'nutrise-auto-gift:campaign-session';
  const persistentTokenKey = 'nutrise-auto-gift:campaigns';
  const cartMutationPattern = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[?#]|$)/i;

  let syncPromise = null;
  let syncAgain = false;
  let syncTimer = null;
  let checkoutInProgress = false;
  let lastEntitlements = [];

  function cartPath(name) {
    return `${root}cart/${name}.js`;
  }

  function normalizeId(value) {
    return String(value ?? '').trim();
  }

  function positiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : 0;
  }

  function money(value) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : 0;
  }

  function safeStorage(storage, action, key, value) {
    try {
      return action === 'get' ? storage.getItem(key) : storage.setItem(key, value);
    } catch (_error) {
      return action === 'get' ? null : undefined;
    }
  }

  function validCampaignToken(value) {
    return /^[a-zA-Z0-9_-]{12,128}$/.test(String(value || '')) ? String(value) : '';
  }

  function captureUrlCampaign() {
    const token = validCampaignToken(new URLSearchParams(window.location.search).get('nag_campaign'));
    if (token) safeStorage(sessionStorage, 'set', sessionTokenKey, token);
    return token;
  }

  function campaignTokens() {
    const now = Date.now();
    const tokens = new Set();
    const sessionToken = validCampaignToken(safeStorage(sessionStorage, 'get', sessionTokenKey));
    if (sessionToken) tokens.add(sessionToken);

    try {
      const records = JSON.parse(safeStorage(localStorage, 'get', persistentTokenKey) || '[]');
      const active = records.filter((record) => validCampaignToken(record.token) && Number(record.expiresAt) > now);
      for (const record of active) tokens.add(record.token);
      safeStorage(localStorage, 'set', persistentTokenKey, JSON.stringify(active));
    } catch (_error) {
      // Invalid legacy storage is ignored.
    }
    return [...tokens];
  }

  function rememberCampaignAccess(rules) {
    const now = Date.now();
    let persistent = [];
    try {
      persistent = JSON.parse(safeStorage(localStorage, 'get', persistentTokenKey) || '[]');
    } catch (_error) {
      persistent = [];
    }

    for (const rule of rules) {
      const access = rule?.access;
      const token = validCampaignToken(access?.token);
      if (!token || access.mode !== 'link') continue;
      safeStorage(sessionStorage, 'set', sessionTokenKey, token);
      if (access.persistence === 'session') continue;

      const duration = access.persistence === '24h'
        ? 24 * 60 * 60 * 1000
        : access.persistence === '7d'
          ? 7 * 24 * 60 * 60 * 1000
          : Math.max(60 * 60 * 1000, (rule.endsAt ? Date.parse(rule.endsAt) : now + 30 * 24 * 60 * 60 * 1000) - now);
      persistent = persistent.filter((record) => record.token !== token);
      persistent.push({ token, expiresAt: now + duration });
    }
    safeStorage(localStorage, 'set', persistentTokenKey, JSON.stringify(persistent));
  }

  function isGiftLine(item) {
    return String(item?.properties?.[properties.marker] || '') === 'true' &&
      normalizeId(item?.properties?.[properties.rule]) !== '';
  }

  function rewardType(item) {
    return String(item?.properties?.[properties.reward] || 'free');
  }

  function isVerifiedRewardLine(item) {
    if (!isGiftLine(item)) return false;
    const original = Number(item.original_line_price || 0);
    const final = Number(item.final_line_price || 0);
    if (!Number.isFinite(original) || !Number.isFinite(final) || original < 0 || final < 0) return false;
    const type = rewardType(item);
    const value = money(item?.properties?.[properties.value]);
    if (type === 'free') return final === 0;
    if (original === 0) return false;
    if (type === 'percentage') return final <= Math.ceil(original * (1 - value / 100)) + 1;
    if (type === 'fixed_price') return final <= Math.round(value * 100 * Number(item.quantity || 0)) + 1;
    return false;
  }

  function normalizeLegacyRule(rule) {
    if (Array.isArray(rule?.tiers)) return rule;
    if (!rule?.buy || !rule?.gift) return null;
    return {
      ...rule,
      priority: 5,
      combinable: true,
      tierMode: 'highest',
      maxPerCustomer: 0,
      customerRemaining: null,
      access: { mode: 'all', persistence: 'session', token: '' },
      tiers: [{
        id: 'legacy',
        label: rule.title,
        maxApplications: rule.usesPerOrderLimit || 1,
        trigger: { type: 'products_any', ...rule.buy, matchMode: 'any', collectionIds: [], collectionProductIds: [], minimumSubtotal: 0 },
        reward: { type: 'free', ...rule.gift, value: 100 },
      }],
    };
  }

  function activeRules(payload) {
    if (!payload?.ok || !Array.isArray(payload.rules)) return [];
    return payload.rules.map(normalizeLegacyRule).filter((rule) =>
      normalizeId(rule?.id) !== '' &&
      Array.isArray(rule.tiers) &&
      rule.tiers.some((tier) => normalizeId(tier?.reward?.variantId) && positiveInteger(tier?.reward?.quantity))
    );
  }

  function regularCartItems(cart) {
    return (cart.items || []).filter((item) => !isGiftLine(item));
  }

  function cartSubtotal(cart) {
    return regularCartItems(cart).reduce((total, item) => total + Number(item.original_line_price || item.line_price || 0), 0) / 100;
  }

  function itemMatchesProduct(item, trigger) {
    if (trigger.all === true) return true;
    const productIds = new Set((trigger.productIds || []).map(normalizeId));
    const variantIds = new Set((trigger.variantIds || []).map(normalizeId));
    return productIds.has(normalizeId(item.product_id)) || variantIds.has(normalizeId(item.variant_id));
  }

  function matchingQuantity(cart, trigger) {
    return regularCartItems(cart).reduce((total, item) => itemMatchesProduct(item, trigger)
      ? total + Number(item.quantity || 0)
      : total, 0);
  }

  function allProductApplications(cart, trigger) {
    const requirements = [
      ...(trigger.productIds || []).map((id) => ({ type: 'product', id: normalizeId(id) })),
      ...(trigger.variantIds || []).map((id) => ({ type: 'variant', id: normalizeId(id) })),
    ];
    if (!requirements.length) return 0;
    return Math.min(...requirements.map((requirement) => {
      const quantity = regularCartItems(cart).reduce((sum, item) => {
        const matches = requirement.type === 'product'
          ? normalizeId(item.product_id) === requirement.id
          : normalizeId(item.variant_id) === requirement.id;
        return matches ? sum + Number(item.quantity || 0) : sum;
      }, 0);
      return Math.floor(quantity / positiveInteger(trigger.quantity));
    }));
  }

  function triggerApplications(cart, trigger) {
    const quantity = positiveInteger(trigger.quantity) || 1;
    if (trigger.type === 'order_value') return cartSubtotal(cart) >= money(trigger.minimumSubtotal) ? 1 : 0;
    if (trigger.type === 'collection') {
      const productIds = new Set((trigger.collectionProductIds || []).map(normalizeId));
      const total = regularCartItems(cart).reduce((sum, item) => productIds.has(normalizeId(item.product_id))
        ? sum + Number(item.quantity || 0)
        : sum, 0);
      return Math.floor(total / quantity);
    }

    const applications = trigger.type === 'products_all' || trigger.matchMode === 'all'
      ? allProductApplications(cart, trigger)
      : Math.floor(matchingQuantity(cart, trigger) / quantity);
    if (trigger.type === 'products_and_value' && cartSubtotal(cart) < money(trigger.minimumSubtotal)) return 0;
    return applications;
  }

  function campaignEntitlements(cart, rule) {
    const eligible = rule.tiers.map((tier) => ({ tier, applications: triggerApplications(cart, tier.trigger) }))
      .filter((entry) => entry.applications > 0);
    const selected = rule.tierMode === 'cumulative' ? eligible : eligible.slice(-1);
    let customerRemaining = Number.isInteger(rule.customerRemaining) ? Math.max(0, rule.customerRemaining) : Number.POSITIVE_INFINITY;

    return selected.map(({ tier, applications }) => {
      const limited = Math.min(applications, positiveInteger(tier.maxApplications) || applications);
      const quantity = Math.min(limited * positiveInteger(tier.reward.quantity), customerRemaining);
      customerRemaining -= quantity;
      return { rule, tier, quantity };
    }).filter((entry) => entry.quantity > 0);
  }

  function campaignsConflict(left, right) {
    if (left.rule.combinable === false || right.rule.combinable === false) return true;
    const leftVariants = new Set(left.entries.map((entry) => normalizeId(entry.tier.reward.variantId)));
    return right.entries.some((entry) => leftVariants.has(normalizeId(entry.tier.reward.variantId)));
  }

  function resolveEntitlements(cart, rules) {
    const campaigns = rules.map((rule) => ({ rule, entries: campaignEntitlements(cart, rule) }))
      .filter((campaign) => campaign.entries.length)
      .sort((left, right) => Number(right.rule.priority || 0) - Number(left.rule.priority || 0));
    const selected = [];
    for (const campaign of campaigns) {
      if (selected.some((chosen) => campaignsConflict(campaign, chosen))) continue;
      selected.push(campaign);
    }
    return selected.flatMap((campaign) => campaign.entries);
  }

  async function fetchJson(url, options = {}) {
    const response = await nativeFetch(url, { credentials: 'same-origin', ...options });
    let payload = null;
    try { payload = await response.json(); } catch (_error) { payload = null; }
    if (!response.ok) throw new Error(payload?.description || payload?.message || payload?.error || `HTTP ${response.status}`);
    return payload;
  }

  async function getRules() {
    const url = new URL(endpoint, window.location.origin);
    for (const token of campaignTokens()) url.searchParams.append('campaign', token);
    const payload = await fetchJson(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const rules = activeRules(payload);
    rememberCampaignAccess(rules);
    return rules;
  }

  function entitlementKey(ruleId, tierId) {
    return `${normalizeId(ruleId)}:${normalizeId(tierId) || 'legacy'}`;
  }

  function lineEntitlementKey(item) {
    return entitlementKey(item?.properties?.[properties.rule], item?.properties?.[properties.tier]);
  }

  function existingGiftLines(cart) {
    const byEntitlement = new Map();
    for (const item of cart.items || []) {
      if (!isGiftLine(item)) continue;
      const key = lineEntitlementKey(item);
      const lines = byEntitlement.get(key) || [];
      lines.push(item);
      byEntitlement.set(key, lines);
    }
    return byEntitlement;
  }

  function lineProperties(entry) {
    const reward = entry.tier.reward;
    return {
      [properties.marker]: 'true',
      [properties.rule]: normalizeId(entry.rule.id),
      [properties.tier]: normalizeId(entry.tier.id),
      [properties.access]: entry.rule.access?.mode === 'link' ? normalizeId(entry.rule.access.token) : '',
      [properties.reward]: reward.type || 'free',
      [properties.value]: String(reward.type === 'free' ? 100 : reward.value),
    };
  }

  function createCartPlan(cart, rules) {
    const existing = existingGiftLines(cart);
    const entitlements = resolveEntitlements(cart, rules);
    const desired = new Map();
    const additions = [];
    const updates = {};

    for (const entry of entitlements) {
      const key = entitlementKey(entry.rule.id, entry.tier.id);
      const rewardType = entry.tier.reward.type || 'free';
      const isAlreadySelected = existing.has(key);
      const quantity = rewardType === 'free' || isAlreadySelected ? entry.quantity : 0;
      desired.set(key, quantity);
      if (quantity > 0 && !isAlreadySelected && rewardType === 'free') {
        additions.push({ id: normalizeId(entry.tier.reward.variantId), quantity, properties: lineProperties(entry) });
      }
    }

    for (const [key, lines] of existing.entries()) {
      const quantity = desired.get(key) || 0;
      const primary = lines[0];
      if (Number(primary.quantity) !== quantity) updates[primary.key] = quantity;
      for (const duplicate of lines.slice(1)) updates[duplicate.key] = 0;
    }

    return { updates, additions, entitlements };
  }

  async function applyCartPlan(plan) {
    let changed = false;
    if (Object.keys(plan.updates).length) {
      await fetchJson(cartPath('update'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Nutrise-Auto-Gift': '1' },
        body: JSON.stringify({ updates: plan.updates }),
      });
      changed = true;
    }
    if (plan.additions.length) {
      await fetchJson(cartPath('add'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Nutrise-Auto-Gift': '1' },
        body: JSON.stringify({ items: plan.additions }),
      });
      changed = true;
    }
    return changed;
  }

  function offerTitle(entry) {
    const reward = entry.tier.reward;
    return reward.productTitle || reward.variantTitle || entry.rule.title;
  }

  function offerBenefit(entry) {
    const reward = entry.tier.reward;
    return reward.type === 'percentage' ? `${reward.value}% günstiger` : `für ${Number(reward.value).toFixed(2)} €`;
  }

  function escapeText(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  }

  function renderOfferPanels(cart, entitlements) {
    document.querySelectorAll('.nutrise-auto-gift-offers').forEach((node) => node.remove());
    const existing = existingGiftLines(cart);
    const offers = entitlements.filter((entry) => entry.tier.reward.type !== 'free' && !existing.has(entitlementKey(entry.rule.id, entry.tier.id)));
    if (!offers.length) return;

    const html = '<div class="nutrise-auto-gift-offers__title">Deine exklusiven Aktionsprodukte</div>' + offers.map((entry) => {
      const key = entitlementKey(entry.rule.id, entry.tier.id);
      return '<div class="nutrise-auto-gift-offer"><div><strong>' + escapeText(offerTitle(entry)) + '</strong><span>' + escapeText(offerBenefit(entry)) + '</span></div><button type="button" data-nutrise-offer="' + escapeText(key) + '">Auswählen</button></div>';
    }).join('');

    const mounts = [document.querySelector('cart-drawer .drawer__footer'), document.querySelector('.cart__footer'), document.querySelector('#main-cart-footer')].filter(Boolean);
    for (const mount of [...new Set(mounts)]) {
      const panel = document.createElement('div');
      panel.className = 'nutrise-auto-gift-offers';
      panel.innerHTML = html;
      mount.prepend(panel);
    }
  }

  async function selectOffer(key) {
    const entry = lastEntitlements.find((candidate) => entitlementKey(candidate.rule.id, candidate.tier.id) === key);
    if (!entry) return;
    await fetchJson(cartPath('add'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Nutrise-Auto-Gift': '1' },
      body: JSON.stringify({ items: [{ id: normalizeId(entry.tier.reward.variantId), quantity: entry.quantity, properties: lineProperties(entry) }] }),
    });
    await runSync();
  }

  function sectionInnerHtml(html, selector) {
    if (!html) return '';
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  function replaceSection(sections, sectionId) {
    const target = document.getElementById(`shopify-section-${sectionId}`);
    if (!target || !sections?.[sectionId]) return;
    const inner = sectionInnerHtml(sections[sectionId], '.shopify-section');
    if (inner) target.innerHTML = inner;
  }

  function refreshCartIcon(sections) {
    const target = document.getElementById('cart-icon-bubble');
    if (!target || !sections?.['cart-icon-bubble']) return;
    const source = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('#cart-icon-bubble');
    if (!source) return;
    target.replaceChildren(...[...source.childNodes].map((node) => node.cloneNode(true)));
    target.className = source.className;
  }

  function reconcileEmptyCartIcon(cart) {
    if (Number(cart?.item_count || 0) !== 0) return;
    document.querySelector('#cart-icon-bubble .cart-count-bubble')?.remove();
  }

  function refreshCartDrawer(sections, cart) {
    const drawer = document.querySelector('cart-drawer');
    const target = drawer?.querySelector('#CartDrawer');
    if (!drawer || !target || !sections?.['cart-drawer']) return;
    const sourceInner = sectionInnerHtml(sections['cart-drawer'], '#CartDrawer');
    if (!sourceInner) return;
    const wasOpen = drawer.classList.contains('active');
    target.innerHTML = sourceInner;
    drawer.classList.toggle('is-empty', Number(cart.item_count || 0) === 0);
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
    if (wasOpen) {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }
  }

  async function refreshCartSurfaces(cart) {
    const sectionIds = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) sectionIds.push('cart-drawer');
    if (document.getElementById('shopify-section-main-cart-items')) sectionIds.push('main-cart-items');
    if (document.getElementById('shopify-section-main-cart-footer')) sectionIds.push('main-cart-footer');
    const uniqueIds = [...new Set(sectionIds)];
    const sections = await fetchJson(`${root}?sections=${encodeURIComponent(uniqueIds.join(','))}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    refreshCartIcon(sections);
    refreshCartDrawer(sections, cart);
    replaceSection(sections, 'main-cart-items');
    replaceSection(sections, 'main-cart-footer');
    reconcileEmptyCartIcon(cart);
    document.dispatchEvent(new CustomEvent('nutrise:auto-gift:updated', { detail: { cart } }));
  }

  async function synchronize() {
    const [rules, cart] = await Promise.all([
      getRules(),
      fetchJson(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }),
    ]);
    const plan = createCartPlan(cart, rules);
    lastEntitlements = plan.entitlements;
    const changed = await applyCartPlan(plan);
    if (!changed) {
      reconcileEmptyCartIcon(cart);
      renderOfferPanels(cart, plan.entitlements);
      return { changed: false, cart };
    }
    const updatedCart = await fetchJson(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    await refreshCartSurfaces(updatedCart);
    const updatedEntitlements = resolveEntitlements(updatedCart, rules);
    lastEntitlements = updatedEntitlements;
    renderOfferPanels(updatedCart, updatedEntitlements);
    return { changed: true, cart: updatedCart };
  }

  async function removeUnverifiedGiftLines(cart) {
    const updates = {};
    for (const item of cart.items || []) {
      if (isGiftLine(item) && !isVerifiedRewardLine(item)) updates[item.key] = 0;
    }
    if (!Object.keys(updates).length) return { removed: false, cart };
    await fetchJson(cartPath('update'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Nutrise-Auto-Gift': '1' },
      body: JSON.stringify({ updates }),
    });
    const updatedCart = await fetchJson(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    await refreshCartSurfaces(updatedCart);
    return { removed: true, cart: updatedCart };
  }

  function runSync() {
    if (syncPromise) { syncAgain = true; return syncPromise; }
    syncPromise = synchronize()
      .catch((error) => { console.warn('Nutrise Auto Gift konnte nicht synchronisiert werden:', error); return { changed: false, error }; })
      .finally(() => { syncPromise = null; if (syncAgain) { syncAgain = false; scheduleSync(80); } });
    return syncPromise;
  }

  function scheduleSync(delay = 120) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(runSync, delay);
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input?.url || '';
  }

  function installFetchObserver() {
    if (window.__nutriseAutoGiftFetchWrapped) return;
    window.__nutriseAutoGiftFetchWrapped = true;
    window.fetch = function nutriseAutoGiftFetch(input, init) {
      const result = nativeFetch(input, init);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      if (method !== 'GET' && cartMutationPattern.test(requestUrl(input))) {
        result.then((response) => { if (response.ok) scheduleSync(100); }).catch(() => {});
      }
      return result;
    };
  }

  async function continueToCheckout(event, trigger) {
    if (checkoutInProgress) return;
    checkoutInProgress = true;
    event.preventDefault();
    trigger?.setAttribute('aria-disabled', 'true');
    try {
      const result = await runSync();
      const cart = result?.cart || await fetchJson(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const validation = await removeUnverifiedGiftLines(cart);
      if (validation.removed) {
        window.alert('Eine Zugabe konnte nicht sicher bestätigt werden und wurde vorsorglich entfernt. Bitte prüfe den Warenkorb und versuche es erneut.');
        return;
      }
      window.location.assign(`${root}checkout`);
    } catch (error) {
      console.warn('Nutrise Auto Gift Checkout-Prüfung fehlgeschlagen:', error);
      window.alert('Der Warenkorb konnte gerade nicht sicher geprüft werden. Bitte versuche es noch einmal.');
    } finally {
      checkoutInProgress = false;
      trigger?.removeAttribute('aria-disabled');
    }
  }

  function installCheckoutGuard() {
    document.addEventListener('click', (event) => {
      const offer = event.target.closest('[data-nutrise-offer]');
      if (offer) { event.preventDefault(); selectOffer(offer.dataset.nutriseOffer).catch((error) => console.warn(error)); return; }
      const trigger = event.target.closest('[name="checkout"], a[href$="/checkout"]');
      if (!trigger || event.defaultPrevented) return;
      continueToCheckout(event, trigger);
    }, true);
    document.addEventListener('submit', (event) => {
      const trigger = event.submitter;
      if (trigger?.name !== 'checkout' || checkoutInProgress) return;
      continueToCheckout(event, trigger);
    }, true);
  }

  captureUrlCampaign();
  installFetchObserver();
  installCheckoutGuard();
  document.addEventListener('cart:refresh', () => scheduleSync(80));
  document.addEventListener('cart:update', () => scheduleSync(80));
  document.addEventListener('DOMContentLoaded', () => scheduleSync(150), { once: true });

  window.NutriseAutoGift = {
    sync: runSync,
    schedule: scheduleSync,
    isGiftLine,
    isVerifiedRewardLine,
    matchingQuantity,
    triggerApplications,
    resolveEntitlements,
    createCartPlan,
  };
})();
